import React, { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { useMedication } from 'lib/hooks/queries/medication/useMedication';

import { DEFAULT_FILTERS } from 'components/Patient/medications/configs';
import medicationsCols from 'components/practice/charts/Overview/widgets/lib/medicationsCols';
import { currentPractice } from 'components/practice/practiceState';
import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import MedicationTableViewSwitch from 'components/shared/Forms/Custom/ProviderMedications/components/MedicationTableViewSwitch/MedicationTableViewSwitch';
import UpsertProviderMedication from 'components/shared/Forms/Custom/ProviderMedications/components/UpsertMedication';
import {
  MEDICATION_PRODUCT_NDC_GROUPING_PROPS,
  useMedicationProductNdcGrouping
} from 'components/shared/Forms/Custom/ProviderMedications/hooks/useMedicationProductNdcGrouping';
import { filterMedicationsByView } from 'components/shared/Forms/Custom/ProviderMedications/lib/medicationTableViews';
import Allowed from 'components/shared/Permissions/Allowed';
import { permissions, userState } from 'components/state';

const MedicationNdcGrouping = ({ rawQueryData }) => {
  const medications = rawQueryData?.medicationHistory?.response?.medications ?? [];
  useMedicationProductNdcGrouping({ medications });
  return null;
};

const PatientHistoryMedications = () => {
  const userPermissions = useRecoilValue(permissions);
  const practice = useRecoilValue(currentPractice);
  const [itemModal, setItemModal] = useState({ open: false, data: null });

  return (
    <Allowed requiredPermissions="patients_medical_history.read">
      <Medications
        itemModal={itemModal}
        setItemModal={setItemModal}
        userPermissions={userPermissions}
        practice={practice}
      />
    </Allowed>
  );
};

const Medications = ({ setItemModal, itemModal, userPermissions, practice }) => {
  const { patient } = useOutletContext();
  const patientId = patient?.id;
  const user = useRecoilValue(userState);

  const queryParams = useMemo(
    () => ({
      patientId
    }),
    [patientId]
  );
  const [medicationView, setMedicationView] = useState(null);

  const formatMedicationsData = useCallback(
    (response) => {
      return filterMedicationsByView(
        response?.medicationHistory?.response?.medications,
        medicationView,
        user
      );
    },
    [medicationView, user]
  );

  return (
    <>
      <AgTablePage
        noTitle
        noNewButton
        noDisplayButton
        patientId={patientId}
        name="patient-history-medications"
        queryKey="direct.format"
        defaultFilters={DEFAULT_FILTERS}
        {...MEDICATION_PRODUCT_NDC_GROUPING_PROPS}
        defaultColumns={medicationsCols({
          setShowMedicationModal: setItemModal,
          userPermissions,
          practice,
          showCheckboxes: false
        })}
        useData={useMedication}
        queryParams={queryParams}
        queryOptions={{ enabled: !!patientId }}
        dependencies={[patientId]}
        formatData={formatMedicationsData}
        headerActions={({ data }) => (
          <>
            <MedicationNdcGrouping rawQueryData={data} />
            <MedicationTableViewSwitch activeView={medicationView} onChange={setMedicationView} />
          </>
        )}
      />
      {itemModal?.open && (
        <UpsertProviderMedication showModal={itemModal} hideModal={() => setItemModal(null)} />
      )}
    </>
  );
};

export default PatientHistoryMedications;
